import React from 'react';

const shimmerClass = 'bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer rounded';

const Base = ({ className = '' }) => (
  <div className={`${shimmerClass} ${className}`} />
);

const Text = ({ width = 'w-full', height = 'h-4', className = '' }) => (
  <Base className={`${width} ${height} ${className}`} />
);

const Circle = ({ size = 'w-10 h-10', className = '' }) => (
  <Base className={`rounded-full ${size} ${className}`} />
);

const Card = ({ className = '' }) => (
  <Base className={`w-full h-28 rounded-lg ${className}`} />
);

const MetricsCards = ({ count = 3 }) => (
  <div className={`grid gap-6 mb-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-${count}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 border-l-4 border-l-indigo-200">
        <div className={`h-3 ${shimmerClass} w-24 mb-3`} />
        <div className={`h-8 ${shimmerClass} w-16 mb-2`} />
        <div className={`h-3 ${shimmerClass} w-20`} />
      </div>
    ))}
  </div>
);

const SessionList = ({ count = 3 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="p-4 border border-gray-200 rounded-lg">
        <div className="flex justify-between mb-2">
          <div className={`h-4 ${shimmerClass} w-40`} />
          <div className={`h-3 ${shimmerClass} w-16`} />
        </div>
        <div className="flex gap-2">
          <div className={`h-5 ${shimmerClass} rounded-full w-16`} />
          <div className={`h-5 ${shimmerClass} w-20`} />
        </div>
      </div>
    ))}
  </div>
);

const BadgeGrid = ({ count = 8 }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex flex-col items-center p-3">
        <Circle size="w-12 h-12" />
        <Text width="w-16" height="h-3" className="mt-2" />
      </div>
    ))}
  </div>
);

const DashboardSkeleton = ({ isElementary = false }) => (
  <div>
    {/* Hero banner */}
    <div className={`${shimmerClass} rounded-xl h-36 mb-6`} />

    {/* Stats cards */}
    <MetricsCards count={isElementary ? 2 : 3} />

    {/* Learning tip */}
    <div className={`${shimmerClass} rounded-lg h-10 mb-6`} />

    {/* Two-column: Badges + Progress */}
    <div className="grid gap-6 mb-6 grid-cols-1 lg:grid-cols-2">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className={`h-5 ${shimmerClass} w-32 mb-4`} />
        <BadgeGrid />
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className={`h-5 ${shimmerClass} w-32 mb-4`} />
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i}>
              <div className={`h-4 ${shimmerClass} w-24 mb-2`} />
              <div className="flex gap-2">
                <div className={`h-8 ${shimmerClass} rounded-full w-20`} />
                <div className={`h-8 ${shimmerClass} rounded-full w-24`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Recent sessions */}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className={`h-5 ${shimmerClass} w-40 mb-4`} />
      <SessionList />
    </div>
  </div>
);

const ProgressSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className={`h-5 ${shimmerClass} w-32 mb-4`} />
    <div className="space-y-3">
      {[1, 2, 3].map(i => (
        <div key={i}>
          <div className={`h-4 ${shimmerClass} w-24 mb-2`} />
          <div className="flex gap-2">
            <div className={`h-8 ${shimmerClass} rounded-full w-20`} />
            <div className={`h-8 ${shimmerClass} rounded-full w-24`} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Skeleton = {
  Text,
  Circle,
  Card,
  MetricsCards,
  SessionList,
  BadgeGrid,
  DashboardSkeleton,
  ProgressSkeleton,
};

export default Skeleton;
